import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { translateError, handleUnauthorizedError } from '@/lib/errorTranslation';
import { LanguageToggle } from '@/components/LanguageToggle';
import { LogOut, Download, Calendar, AlertCircle, Eye } from 'lucide-react';

/**
 * Parse a date string (YYYY-MM-DD) as local time, not UTC.
 * This prevents timezone issues where dates shift by a day.
 */
function parseLocalDate(dateStr: string): Date {
  if (!dateStr) return new Date();
  // If it's already a full ISO datetime (with T) or SQLite datetime (with space), just parse it
  if (dateStr.includes('T') || dateStr.includes(' ')) {
    return new Date(dateStr.replace(' ', 'T'));
  }
  // For date-only strings, parse as local time
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function ParentDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, isLoading } = useAuth0();
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for Auth0 to finish loading
    if (isLoading) return;

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate('/parent/login');
      return;
    }

    loadDashboard();
  }, [isAuthenticated, isLoading, navigate]);

  const loadDashboard = async () => {
    try {
      const result:any = await api.getParentDashboard();
      setPatients(result.patients);
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'parent')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPassport = async (patientId: number) => {
    try {
      // Download PDF directly from the API
      const blob = await api.downloadVaccinePassport(patientId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Find patient to get their name for the filename
      const patient = patients.find(p => p.id === patientId);
      const today = new Date().toISOString().split('T')[0];
      
      let fileName: string;
      if (patient?.name) {
        const safeName = patient.name
          .replaceAll(/[^a-zA-Z0-9\s]/g, '')
          .replaceAll(/\s+/g, '-')
          .toLowerCase()
          .trim();
        
        fileName = safeName 
          ? `cartilla-de-vacunacion-${safeName}-${today}.pdf`
          : `cartilla-de-vacunacion-${today}.pdf`;
      } else {
        fileName = `cartilla-de-vacunacion-${today}.pdf`;
      }
      
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();

      toast({
        title: t('app.success'),
        description: t('parent.downloadPassport'),
      });
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'parent')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    }
  };

  const handleLogout = () => {
    navigate('/logout');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('app.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <img src="https://pub-327c49603f8c4373ac8b69237691fafc.r2.dev/rds-logo.png" alt="Logo" className="h-12 w-12" />
            {t('parent.children')}
          </h1>
          <div className="flex gap-2">
            <LanguageToggle />
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              {t('nav.logout')}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {patients.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-gray-500">{t('parent.noChildren')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {patients.map((patient: any) => (
              <Card key={patient.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{patient.name}</span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/parent/patients/${patient.id}`)}
                        title={t('common.view')}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadPassport(patient.id)}
                        title={t('parent.downloadPassport')}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {t('patient.dob')}: {parseLocalDate(patient.dob).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t('patient.doctor')}: {patient.doctor_name}
                    </p>
                  </div>

                  {/* Upcoming Vaccines */}
                  <div>
                    <h4 className="text-sm font-semibold mb-2">{t('vaccine.upcoming')}</h4>
                    {patient.upcoming_doses && patient.upcoming_doses.length > 0 ? (
                      <ul className="space-y-1">
                        {patient.upcoming_doses.slice(0, 3).map((dose: any, idx: number) => (
                          <li
                            key={idx}
                            className={`text-sm ${
                              dose.is_overdue ? 'text-red-600 font-semibold' : 'text-gray-700'
                            }`}
                          >
                            {dose.is_overdue && <AlertCircle className="inline h-4 w-4 mr-1" />}
                            {dose.vaccine_label} - {t('vaccine.doseNumber')} {dose.dose_number}
                            {dose.due_date && (
                              <span className="text-xs ml-1">
                                ({parseLocalDate(dose.due_date).toLocaleDateString()})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">{t('vaccine.noVaccines')}</p>
                    )}
                  </div>

                  {/* Completed Vaccines */}
                  <div>
                    <h4 className="text-sm font-semibold mb-2">{t('parent.completedVaccines')}</h4>
                    <p className="text-sm text-gray-700">
                      {patient.completed_doses?.length || 0} {t('vaccine.administered')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
